import { useRef, useState, useEffect } from 'react';

import { reindexUrlCollectionItems } from '@home/utils/urlCollectionData';

import useShortcutCollection from './useShortcutCollection';
import useShortcutImport from './useShortcutImport';

import type { IAnalysisUrlInfoResult } from '@home/enums/urlCollection';
import type { IUrlCollectionItem } from '@home/types/urlCollection';

enum MyShortcutsFlowStatus {
  InitialLoading = 'InitialLoading',
  Ready = 'Ready',
  Editing = 'Editing',
  Analysing = 'Analysing',
  Saving = 'Saving',
}

interface IUseMyShortcutsControllerOptions {
  maxCount: number;
}

interface IMyShortcutsControllerState {
  status: MyShortcutsFlowStatus;
  savedShortcuts: IUrlCollectionItem[];
  draftShortcuts: IUrlCollectionItem[];
}

interface IMyShortcutsViewState {
  currentShortcuts: IUrlCollectionItem[];
  shortcutsLoading: boolean;
  showAction: boolean;
  showAddCard: boolean;
}

interface IMyShortcutsEditModalProps {
  visible: boolean;
  items: IUrlCollectionItem[];
  initialItems: IUrlCollectionItem[];
  loading: boolean;
  disabledClose: boolean;
  onAdd: () => void;
  onCancel: () => void;
  onItemsChange: (items: IUrlCollectionItem[]) => void;
  onItemClick: (item: IUrlCollectionItem) => void;
  onConfirm: (items: IUrlCollectionItem[]) => void;
}

interface IMyShortcutsAnalyseModalProps {
  visible: boolean;
  url: string;
  pageName: string;
  analysisResult: IAnalysisUrlInfoResult | null;
  importLoading: boolean;
  confirmLoading: boolean;
  shortcutImportErrorText: string;
  onUrlChange: (value: string) => void;
  onPageNameChange: (value: string) => void;
  onImport: () => void;
  onConfirm: () => void;
  onClose: () => void;
}

interface IMyShortcutsControllerHandlers {
  handleOpenEditShortcutsModal: () => void;
  handleShortcutClick: (item: IUrlCollectionItem) => void;
}

interface IMyShortcutsControllerReturn {
  viewState: IMyShortcutsViewState;
  editModalProps: IMyShortcutsEditModalProps;
  analyseModalProps: IMyShortcutsAnalyseModalProps;
  handlers: IMyShortcutsControllerHandlers;
}

const useMyShortcutsController = (options: IUseMyShortcutsControllerOptions): IMyShortcutsControllerReturn => {
  const { maxCount } = options;
  const { loadShortcuts, saveShortcuts, handleShortcutClick } = useShortcutCollection();
  const [controllerState, setControllerState] = useState<IMyShortcutsControllerState>({
    status: MyShortcutsFlowStatus.InitialLoading,
    savedShortcuts: [],
    draftShortcuts: [],
  });
  const loadSeqRef = useRef(0);
  const statusRef = useRef(controllerState.status);

  statusRef.current = controllerState.status;

  const { status, savedShortcuts, draftShortcuts } = controllerState;
  const isShortcutsLoading = status === MyShortcutsFlowStatus.InitialLoading;
  const isSaving = status === MyShortcutsFlowStatus.Saving;
  const isEditModalVisible =
    status === MyShortcutsFlowStatus.Editing ||
    status === MyShortcutsFlowStatus.Analysing ||
    status === MyShortcutsFlowStatus.Saving;
  const isAnalyseModalVisible = status === MyShortcutsFlowStatus.Analysing;

  const canOperateShortcutActions = (): boolean => {
    return !isShortcutsLoading && !isSaving;
  };

  const handleDraftShortcutsChange = (items: IUrlCollectionItem[]): void => {
    setControllerState((prevState) => {
      return {
        ...prevState,
        draftShortcuts: items,
      };
    });
  };

  const handleImportConfirmed = (): void => {
    statusRef.current = MyShortcutsFlowStatus.Editing;
    setControllerState((prevState) => {
      return {
        ...prevState,
        status: MyShortcutsFlowStatus.Editing,
      };
    });
  };

  const {
    url,
    pageName,
    analysisResult,
    importLoading,
    confirmLoading,
    shortcutImportErrorText,
    resetImportState,
    handleImport,
    handleAnalyseConfirm,
    handleUrlChange,
    handlePageNameChange,
  } = useShortcutImport({
    draftShortcuts,
    maxCount,
    onDraftShortcutsChange: handleDraftShortcutsChange,
    onImportConfirmed: handleImportConfirmed,
  });

  useEffect(() => {
    const currentSeq = loadSeqRef.current + 1;
    loadSeqRef.current = currentSeq;

    statusRef.current = MyShortcutsFlowStatus.InitialLoading;
    setControllerState((prevState) => {
      return {
        ...prevState,
        status: MyShortcutsFlowStatus.InitialLoading,
        savedShortcuts: [],
        draftShortcuts: [],
      };
    });

    void (async () => {
      const nextShortcuts = await loadShortcuts();

      if (loadSeqRef.current !== currentSeq) {
        return;
      }

      if (nextShortcuts) {
        statusRef.current = MyShortcutsFlowStatus.Ready;
        setControllerState({
          status: MyShortcutsFlowStatus.Ready,
          savedShortcuts: nextShortcuts,
          draftShortcuts: nextShortcuts,
        });
        return;
      }

      statusRef.current = MyShortcutsFlowStatus.Ready;
      setControllerState((prevState) => {
        return {
          ...prevState,
          status: MyShortcutsFlowStatus.Ready,
          savedShortcuts: [],
          draftShortcuts: [],
        };
      });
    })();

    return () => {
      loadSeqRef.current += 1;
    };
  }, []);

  const handleOpenEditShortcutsModal = (): void => {
    if (!canOperateShortcutActions()) {
      return;
    }

    resetImportState();
    statusRef.current = MyShortcutsFlowStatus.Editing;
    setControllerState((prevState) => {
      return {
        ...prevState,
        status: MyShortcutsFlowStatus.Editing,
        draftShortcuts: prevState.savedShortcuts,
      };
    });
  };

  const handleCloseEditShortcutsModal = (): void => {
    if (statusRef.current === MyShortcutsFlowStatus.Saving) {
      return;
    }

    resetImportState();
    statusRef.current = MyShortcutsFlowStatus.Ready;
    setControllerState((prevState) => {
      return {
        ...prevState,
        status: MyShortcutsFlowStatus.Ready,
        draftShortcuts: prevState.savedShortcuts,
      };
    });
  };

  const handleOpenAnalyseShortcutsModal = (): void => {
    if (!canOperateShortcutActions()) {
      return;
    }

    resetImportState();
    statusRef.current = MyShortcutsFlowStatus.Analysing;
    setControllerState((prevState) => {
      return {
        ...prevState,
        status: MyShortcutsFlowStatus.Analysing,
      };
    });
  };

  const handleCloseAnalyseShortcutsModal = (): void => {
    resetImportState();
    statusRef.current = MyShortcutsFlowStatus.Editing;
    setControllerState((prevState) => {
      return {
        ...prevState,
        status: MyShortcutsFlowStatus.Editing,
      };
    });
  };

  const handleImportShortcuts = (): void => {
    void handleImport();
  };

  const handleConfirmAnalyseShortcuts = (): void => {
    void handleAnalyseConfirm();
  };

  const handleSaveShortcuts = (items: IUrlCollectionItem[]): void => {
    if (statusRef.current === MyShortcutsFlowStatus.Saving) {
      return;
    }

    const requestItems = reindexUrlCollectionItems(items);

    statusRef.current = MyShortcutsFlowStatus.Saving;
    setControllerState((prevState) => {
      return {
        ...prevState,
        status: MyShortcutsFlowStatus.Saving,
        draftShortcuts: requestItems,
      };
    });

    void (async () => {
      const nextSavedShortcuts = await saveShortcuts(requestItems);

      if (!nextSavedShortcuts) {
        statusRef.current = MyShortcutsFlowStatus.Editing;
        setControllerState((prevState) => {
          return {
            ...prevState,
            status: MyShortcutsFlowStatus.Editing,
            draftShortcuts: requestItems,
          };
        });
        return;
      }

      resetImportState();
      statusRef.current = MyShortcutsFlowStatus.Ready;
      setControllerState({
        status: MyShortcutsFlowStatus.Ready,
        savedShortcuts: nextSavedShortcuts,
        draftShortcuts: nextSavedShortcuts,
      });
    })();
  };

  const currentShortcuts = savedShortcuts;
  const showAction = canOperateShortcutActions();
  const showAddCard =
    showAction && Array.isArray(currentShortcuts) && currentShortcuts.length > 0 && currentShortcuts.length < maxCount;

  return {
    viewState: {
      currentShortcuts,
      shortcutsLoading: isShortcutsLoading,
      showAction,
      showAddCard,
    },
    editModalProps: {
      visible: isEditModalVisible,
      items: draftShortcuts,
      initialItems: savedShortcuts,
      loading: isSaving,
      disabledClose: isSaving,
      onAdd: handleOpenAnalyseShortcutsModal,
      onCancel: handleCloseEditShortcutsModal,
      onItemsChange: handleDraftShortcutsChange,
      onItemClick: handleShortcutClick,
      onConfirm: handleSaveShortcuts,
    },
    analyseModalProps: {
      visible: isAnalyseModalVisible,
      url,
      pageName,
      analysisResult,
      importLoading,
      confirmLoading,
      shortcutImportErrorText,
      onUrlChange: handleUrlChange,
      onPageNameChange: handlePageNameChange,
      onImport: handleImportShortcuts,
      onConfirm: handleConfirmAnalyseShortcuts,
      onClose: handleCloseAnalyseShortcutsModal,
    },
    handlers: {
      handleOpenEditShortcutsModal,
      handleShortcutClick,
    },
  };
};

export default useMyShortcutsController;
